import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	TEasInfoGroup,
	TEasInfoListRequest,
	TEasInfoListResponsePayload,
	TEasInfoPatchRequest,
	TEasInfoUpdateRequest,
} from './types';
import { getEasInfoLastListParams } from './selectors';
import {
	getEasInfoGroupsFailure,
	getEasInfoGroupsRequest,
	getEasInfoGroupsSuccess,
	getEasInfoListFailure,
	getEasInfoListRequest,
	getEasInfoListSuccess,
	patchEasInfoFailure,
	patchEasInfoRequest,
	patchEasInfoSuccess,
	updateEasInfoFailure,
	updateEasInfoRequest,
	updateEasInfoSuccess,
} from './reducer';

function* getEasInfoListWorker({ payload }: PayloadAction<TEasInfoListRequest | undefined>) {
	try {
		const response: TEasInfoListResponsePayload = yield call(api.easInfo.getEasInfoList, payload);
		yield put(getEasInfoListSuccess(response));
	} catch (error) {
		yield put(getEasInfoListFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error.message ?? 'Failed to load EAS info'),
				'error',
			);
		}
	}
}

function* getEasInfoGroupsWorker() {
	try {
		const response: TEasInfoGroup[] = yield call(api.easInfo.getEasInfoGroups);
		yield put(getEasInfoGroupsSuccess(response));
	} catch (error) {
		yield put(getEasInfoGroupsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error.message ?? 'Failed to load EAS info groups'),
				'error',
			);
		}
	}
}

function* refetchEasInfoList() {
	const lastParams: TEasInfoListRequest | null = yield select(getEasInfoLastListParams);
	const response: TEasInfoListResponsePayload = yield call(
		api.easInfo.getEasInfoList,
		lastParams ?? undefined,
	);
	yield put(getEasInfoListSuccess(response));
}

function* patchEasInfoWorker({ payload }: PayloadAction<TEasInfoPatchRequest>) {
	try {
		yield call(api.easInfo.patchEasInfo, payload);
		yield call(refetchEasInfoList);
		yield put(patchEasInfoSuccess());
		notificationContainer('EAS info updated successfully', 'success');
	} catch (error) {
		yield put(patchEasInfoFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error.message ?? 'Failed to update EAS info'),
				'error',
			);
		}
	}
}

function* updateEasInfoWorker({ payload }: PayloadAction<TEasInfoUpdateRequest>) {
	try {
		yield call(api.easInfo.updateEasInfo, payload);
		yield call(refetchEasInfoList);
		yield put(updateEasInfoSuccess());
		notificationContainer('EAS info created successfully', 'success');
	} catch (error) {
		yield put(updateEasInfoFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error.message ?? 'Failed to create EAS info'),
				'error',
			);
		}
	}
}

export function* easInfoSaga() {
	yield takeLatest(getEasInfoListRequest.type, getEasInfoListWorker);
	yield takeLatest(getEasInfoGroupsRequest.type, getEasInfoGroupsWorker);
	yield takeLatest(patchEasInfoRequest.type, patchEasInfoWorker);
	yield takeLatest(updateEasInfoRequest.type, updateEasInfoWorker);
}
